import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Check, Circle, Languages, Pencil, Trash2, Type, Undo2, X } from "lucide-react";

const COLORS = ["#ef4444", "#facc15", "#ffffff", "#22c55e"];
const TOOLS = [["ellipse", Circle, "圈选", "Circle"], ["arrow", ArrowUpRight, "箭头", "Arrow"], ["pen", Pencil, "画笔", "Pen"], ["text", Type, "文字", "Text"]];
export const QUICK_PHRASES = [
  ["裂缝", "Crack"], ["渗水", "Water leak"], ["未按图施工", "Not per plans"], ["需整改", "Needs rework"],
  ["安全隐患", "Safety hazard"], ["已整改", "Fixed"], ["待验收", "Pending inspection"], ["尺寸偏差", "Dimension off"],
];

export function drawAnnotations(ctx, w, h, shapes, caption, lang = "zh") {
  const lw = Math.max(3, Math.round(Math.min(w, h) / 150));
  const fs = Math.max(18, Math.round(Math.min(w, h) / 25));
  ctx.lineCap = "round"; ctx.lineJoin = "round";
  for (const s of shapes || []) {
    ctx.strokeStyle = s.color; ctx.fillStyle = s.color; ctx.lineWidth = lw;
    ctx.shadowColor = "rgba(0,0,0,.6)"; ctx.shadowBlur = lw;
    if (s.type === "ellipse") {
      const cx = (s.x1 + s.x2) / 2, cy = (s.y1 + s.y2) / 2, rx = Math.abs(s.x2 - s.x1) / 2, ry = Math.abs(s.y2 - s.y1) / 2;
      if (rx < 2 && ry < 2) continue;
      ctx.beginPath(); ctx.ellipse(cx, cy, Math.max(rx, 2), Math.max(ry, 2), 0, 0, Math.PI * 2); ctx.stroke();
    } else if (s.type === "arrow") {
      const dx = s.x2 - s.x1, dy = s.y2 - s.y1, len = Math.hypot(dx, dy);
      if (len < 4) continue;
      const a = Math.atan2(dy, dx), hl = Math.max(lw * 4, Math.min(len / 3, lw * 8));
      ctx.beginPath(); ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(s.x2, s.y2);
      ctx.lineTo(s.x2 - hl * Math.cos(a - Math.PI / 6), s.y2 - hl * Math.sin(a - Math.PI / 6));
      ctx.lineTo(s.x2 - hl * Math.cos(a + Math.PI / 6), s.y2 - hl * Math.sin(a + Math.PI / 6));
      ctx.closePath(); ctx.fill();
    } else if (s.type === "pen" && s.points?.length > 1) {
      ctx.beginPath(); ctx.moveTo(s.points[0][0], s.points[0][1]);
      for (const [x, y] of s.points.slice(1)) ctx.lineTo(x, y);
      ctx.stroke();
    } else if (s.type === "text" && s.text) {
      ctx.font = `bold ${fs}px -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif`;
      ctx.textBaseline = "top"; ctx.shadowBlur = lw * 1.5;
      ctx.fillText(s.text, s.x1, s.y1);
    }
  }
  ctx.shadowBlur = 0;
  if (caption) {
    const cf = Math.max(16, Math.round(Math.min(w, h) / 28)), pad = Math.round(cf * 0.6), bh = cf + pad * 2;
    ctx.fillStyle = "rgba(15,23,42,.72)"; ctx.fillRect(0, 0, w, bh);
    ctx.fillStyle = "#fff"; ctx.font = `${cf}px -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif`; ctx.textBaseline = "middle";
    ctx.fillText(`${lang !== "zh" ? "Note" : "备注"}: ${caption}`, pad, bh / 2, w - pad * 2);
  }
}

export const renderAnnotated = (shot) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0);
    drawAnnotations(ctx, c.width, c.height, shot.shapes, shot.caption, shot.lang);
    c.toBlob((b) => resolve(b ? new File([b], shot.file.name.replace(/\.jpe?g$/i, "") + "_marked.jpg", { type: "image/jpeg" }) : shot.file), "image/jpeg", 0.9);
  };
  img.onerror = () => resolve(shot.file);
  img.src = shot.url;
});

export const PhotoAnnotator = ({ shot, zh, onSave, onClose }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [shapes, setShapes] = useState(shot.shapes || []);
  const [caption, setCaption] = useState(shot.caption || "");
  const [lang, setLang] = useState(shot.lang || (zh ? "zh" : "en"));
  const [tool, setTool] = useState("ellipse");
  const [color, setColor] = useState(COLORS[0]);
  const [cur, setCur] = useState(null);
  const [textAt, setTextAt] = useState(null);
  const [textVal, setTextVal] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.onload = () => { imgRef.current = img; const c = canvasRef.current; if (c) { c.width = img.naturalWidth; c.height = img.naturalHeight; } setReady(true); };
    img.src = shot.url;
  }, [shot.url]);

  useEffect(() => {
    const c = canvasRef.current, img = imgRef.current;
    if (!c || !img || !ready) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0);
    drawAnnotations(ctx, c.width, c.height, cur ? [...shapes, cur] : shapes, caption, lang);
  }, [shapes, cur, caption, lang, ready]);

  const pos = (e) => {
    const c = canvasRef.current, r = c.getBoundingClientRect();
    return [Math.round((e.clientX - r.left) * (c.width / r.width)), Math.round((e.clientY - r.top) * (c.height / r.height))];
  };
  const down = (e) => {
    if (!ready) return;
    e.preventDefault();
    const [x, y] = pos(e);
    if (tool === "text") { setTextAt([x, y, e.clientX, e.clientY]); setTextVal(""); return; }
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setCur(tool === "pen" ? { type: "pen", color, points: [[x, y]] } : { type: tool, color, x1: x, y1: y, x2: x, y2: y });
  };
  const move = (e) => {
    if (!cur) return;
    const [x, y] = pos(e);
    setCur((s) => (s.type === "pen" ? { ...s, points: [...s.points, [x, y]] } : { ...s, x2: x, y2: y }));
  };
  const up = () => {
    if (!cur) return;
    const ok = cur.type === "pen" ? cur.points.length > 1 : Math.hypot(cur.x2 - cur.x1, cur.y2 - cur.y1) > 3;
    if (ok) setShapes((s) => [...s, cur]);
    setCur(null);
  };
  const commitText = () => {
    if (textAt && textVal.trim()) setShapes((s) => [...s, { type: "text", color, x1: textAt[0], y1: textAt[1], text: textVal.trim().slice(0, 60) }]);
    setTextAt(null); setTextVal("");
  };
  const pickPhrase = (p) => {
    const word = lang !== "zh" ? p[1] : p[0];
    if (textAt) { setTextVal((v) => (v ? `${v} ${word}` : word)); return; }
    setCaption((c) => (c ? `${c}${lang !== "zh" ? ", " : "，"}${word}` : word).slice(0, 80));
  };

  return (
    <div className="fixed inset-0 z-[10002] bg-black flex flex-col" data-testid="photo-annotator">
      <div className="mobile-safe-topbar flex items-center gap-1.5 px-3 py-2 flex-wrap text-white/90 bg-black">
        <span className="text-xs font-semibold mr-1">{zh ? "照片标注" : "Annotate"}</span>
        {TOOLS.map(([k, Icon, z, en]) => (
          <button key={k} data-testid={`annot-tool-${k}`} onClick={() => setTool(k)} title={zh ? z : en}
            className={`h-8 px-2 rounded-lg inline-flex items-center gap-1 text-[11px] ${tool === k ? "bg-white text-slate-900" : "bg-white/10 hover:bg-white/20"}`}><Icon className="h-3.5 w-3.5" />{zh ? z : en}</button>
        ))}
        <span className="mx-1 h-5 w-px bg-white/20" />
        {COLORS.map((c) => (
          <button key={c} data-testid={`annot-color-${c.slice(1)}`} onClick={() => setColor(c)} style={{ background: c }}
            className={`h-6 w-6 rounded-full border-2 ${color === c ? "border-white scale-110" : "border-transparent opacity-80"}`} />
        ))}
        <span className="mx-1 h-5 w-px bg-white/20" />
        <button data-testid="annot-undo" onClick={() => setShapes((s) => s.slice(0, -1))} disabled={!shapes.length} className="h-8 px-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 inline-flex items-center gap-1 text-[11px]"><Undo2 className="h-3.5 w-3.5" />{zh ? "撤销" : "Undo"}</button>
        <button data-testid="annot-clear" onClick={() => setShapes([])} disabled={!shapes.length} className="h-8 px-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 inline-flex items-center gap-1 text-[11px]"><Trash2 className="h-3.5 w-3.5" />{zh ? "清空" : "Clear"}</button>
        <span className="ml-auto text-[10px] text-white/50" data-testid="annot-count">{shapes.length} {zh ? "个标注" : "marks"}</span>
        <button data-testid="annot-close" onClick={onClose} className="p-1.5 rounded-full bg-white/10 hover:bg-white/20"><X className="h-4 w-4" /></button>
      </div>
      <div className="relative flex-1 min-h-0 flex items-center justify-center p-2">
        <canvas ref={canvasRef} data-testid="annot-canvas" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
          className="max-h-full max-w-full object-contain rounded-md touch-none select-none" style={{ touchAction: "none", cursor: tool === "text" ? "text" : "crosshair" }} />
        {textAt && (
          <input autoFocus data-testid="annot-text-input" value={textVal} onChange={(e) => setTextVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commitText(); if (e.key === "Escape") setTextAt(null); }} onBlur={commitText} onPointerDown={(e) => e.stopPropagation()}
            placeholder={zh ? "输入文字后回车" : "Type, Enter"} style={{ left: textAt[2], top: textAt[3], color }}
            className="fixed z-10 text-sm font-bold bg-black/70 border border-white/40 rounded px-2 py-1 outline-none w-48" />
        )}
      </div>
      <div className="flex items-center gap-1.5 px-3 pt-2 flex-wrap bg-black" data-testid="annot-phrases">
        <button data-testid="annot-lang-toggle" onClick={() => setLang((l) => (l === "zh" ? "en" : "zh"))} title={zh ? "快捷词语言" : "Phrase language"}
          className="h-7 px-2 rounded-full bg-white/15 text-white text-[10px] font-bold inline-flex items-center gap-1 hover:bg-white/25">
          <Languages className="h-3 w-3" />{lang === "zh" ? "中文" : "EN"}</button>
        <span className="text-[10px] text-white/40">{textAt ? (zh ? "点词填入文字框" : "tap → text box") : (zh ? "点词加入备注" : "tap → caption")}</span>
        {QUICK_PHRASES.map((p, i) => (
          <button key={p[1]} data-testid={`annot-phrase-${i}`} onPointerDown={(e) => e.preventDefault()} onClick={() => pickPhrase(p)}
            className="h-7 px-2.5 rounded-full bg-white/10 text-white text-[11px] hover:bg-emerald-500/80 transition-colors">{lang !== "zh" ? p[1] : p[0]}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 px-3 py-2.5 bg-black">
        <input data-testid="annot-caption" value={caption} onChange={(e) => setCaption(e.target.value.slice(0, 80))}
          placeholder={zh ? "照片备注（选填，会印在照片顶部）例：北墙裂缝约 2mm" : "Caption (stamped at top)"}
          className="flex-1 text-[12px] rounded-lg bg-white/10 text-white placeholder:text-white/40 border border-white/15 px-3 py-2 outline-none focus:border-white/40" />
        <button data-testid="annot-save" onClick={() => onSave(shapes, caption.trim(), lang)}
          className="text-[12px] font-semibold rounded-full px-4 py-2 bg-emerald-500 text-white hover:bg-emerald-400 inline-flex items-center gap-1"><Check className="h-4 w-4" />{zh ? "完成标注" : "Done"}</button>
      </div>
    </div>
  );
};
